import * as yup from "yup";

export const step1Schema = yup.object({
  title: yup.string().trim().required("Experience title is required"),

  location: yup
    .object({
      placeId: yup.string().required(),
      fullAddress: yup.string().required(),
      latitude: yup.number().required(),
      longitude: yup.number().required(),
      city: yup.string().nullable(),
      area: yup.string().nullable(),
      country: yup.string().nullable(),
    })
    .required("Location is required"),

  description: yup.string().required("Description is required"),

  price: yup
    .number()
    .typeError("Price must be a number")
    .positive()
    .required("Price is required"),

  category: yup.string().required("Category is required"),

  difficulty: yup.string().required("Difficulty is required"),

  // 🔒 Fixed system fields (still validated)
  baseDuration: yup.number().required(),
  baseGroupSize: yup.number().required(),
  bufferTime: yup.number().required(),
});

export const step2Schema = yup.object({
  // MEDIA
  images: yup
    .array()
    .min(1, "At least one image is required")
    .max(5, "You can upload up to 5 images only")
    .required("Images are required"),

  // AVAILABILITY RULES
  availabilityRules: yup
    .array()
    .of(
      yup.object({
        startDate: yup
          .date()
          .typeError("Start date is required")
          .required("Start date is required"),

        endDate: yup
          .date()
          .typeError("End date is required")
          .required("End date is required")
          .test(
            "max-range",
            "Date range cannot exceed 1 year",
            function (endDate) {
              const { startDate } = this.parent;
              if (!startDate || !endDate) return true;

              const diff =
                new Date(endDate).getTime() - new Date(startDate).getTime();

              // 365 days
              return diff <= 365 * 24 * 60 * 60 * 1000;
            },
          ),

        days: yup
          .array()
          .of(yup.string())
          .min(1, "Select at least one open day")
          .required("Open days are required"),

        startTime: yup.string().required("Start time is required"),

        endTime: yup
          .string()
          .required("End time is required")
          .test(
            "end-after-start",
            "End time must be after start time",
            function (endTime) {
              const { startTime } = this.parent;
              if (!startTime || !endTime) return true;
              // simple lexicographical compare works for HH:mm
              return endTime > startTime;
            },
          ),
      }),
    )
    .min(1, "At least one availability rule is required")
    .required("Availability rules are required"),

  // GROUP SIZE & DURATION RULES
  // durationRules: yup
  //   .array()
  //   .of(
  //     yup.object({
  //       min: yup
  //         .number()
  //         .typeError("Min guests must be a number")
  //         .integer("Min guests must be an integer")
  //         .positive("Min guests must be greater than 0")
  //         .required("Min guests required"),

  //       max: yup
  //         .number()
  //         .typeError("Max guests must be a number")
  //         .integer("Max guests must be an integer")
  //         .positive("Max guests must be greater than 0")
  //         .moreThan(yup.ref("min"), "Max must be greater than min")
  //         .required("Max guests required"),

  //       duration: yup
  //         .number()
  //         .typeError("Duration must be a number")
  //         .positive("Duration must be greater than 0")
  //         .required("Duration is required"),
  //     }),
  //   )
  //   .min(1, "Add at least one group size rule")
  //   .required("Duration rules are required"),

  // OPTIONAL ADD-ONS
  addOns: yup
    .array()
    .of(
      yup.object({
        name: yup.string().trim().required("Add-on name is required"),
        price: yup
          .number()
          .typeError("Add-on price must be a number")
          .positive("Add-on price must be greater than 0")
          .required("Add-on price is required"),
      }),
    )
    .optional(),
  availabilityOverrides: yup
    .array()
    .of(
      yup.object({
        date: yup.string().required("Override date is required"),

        overrideType: yup
          .string()
          .oneOf(["FullDayClosed", "CustomWindow", "BlockedRange"])
          .required("Override type is required"),

        startTime: yup.string().nullable(),

        endTime: yup
          .string()
          .nullable()
          .test(
            "end-after-start",
            "End time must be after start time",
            function (endTime) {
              const { startTime, overrideType } = this.parent;

              // Closed day ke liye time irrelevant
              if (overrideType === "Closed") return true;

              if (!startTime || !endTime) return true;
              return endTime > startTime;
            },
          ),
      }),
    )
    .optional(),

  maxGuestCount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .when("isPrivate", {
      is: false,
      then: (schema) =>
        schema
          .required("Max guest capacity is required")
          .moreThan(0, "Must be greater than 0"),
      otherwise: (schema) => schema.notRequired(),
    }),
  isPrivate: yup.boolean().default(false),
});

export const fullSchema = step1Schema.concat(step2Schema);
